import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';

import toastConfig from '../components/shared/constants/toaster-config';
import {
  fillSeats,
  getMyCapacity,
  releaseConversation,
  getAgentLoad,
  getLimitConfig,
  saveLimitConfig,
  getConversationLimitErrorCode,
  conversationLimitErrorMessage,
} from '../utils/conversationLimit/conversationLimitApi';
import { buildConfigPayload } from '../utils/conversationLimit/configValidation';
import { removeConversationLocallyAction } from './conversationLimitListActions';
import reducer, { conversationLimitActions as A } from './conversationLimitReducer';

/**
 * Conversation limit ("modo cola") duck — thunks. The pure reducer and its
 * action creators live in `conversationLimitReducer.js`; Pusher handlers in
 * `conversationLimitRealtime.js`. Every thunk is a no-op for the chat when
 * the backend says `enabled: false` (R8).
 */

export { CONVERSATION_LIMIT_INITIAL_STATE, conversationLimitActions } from './conversationLimitReducer';
export default reducer;

const isEnabled = (getState) => getState().conversationLimit?.enabled === true;

/** Every 60 s while the chat is mounted. A failed request keeps the last state. */
export const heartbeatAction = () => async (dispatch) => {
  const view = await getMyCapacity();
  if (!view.failed) dispatch(A.viewReceived(view));
};

/** R7: after a Pusher reconnect, events may have been lost — resync seats. */
export const resyncSeatsAction = () => async (dispatch, getState) => {
  if (!isEnabled(getState)) return;
  const view = await fillSeats();
  if (!view.failed) dispatch(A.viewReceived(view));
};

const reportUnexpected = (error, op) => {
  if (!getConversationLimitErrorCode(error)) {
    Sentry.captureException(error, { tags: { component: 'conversationLimit', op } });
  }
};

/** F5: the agent lets go of a conversation; it goes back to the queue. */
export const releaseConversationAction = (clientId, reason, comment) => async (dispatch) => {
  // The `conversationUnseated` event usually lands before this response.
  dispatch(A.ownUnseatStarted(clientId));
  try {
    const view = await releaseConversation({ clientId, reason, comment });
    dispatch(A.viewReceived(view));
    dispatch(A.seatRemoved(clientId));
    dispatch(removeConversationLocallyAction(clientId, { closeIfOpen: true }));
    toast.success('Conversación liberada. Te llegará la siguiente de la cola.', toastConfig);
    return { ok: true, code: null };
  } catch (error) {
    dispatch(A.ownUnseatSettled(clientId));
    reportUnexpected(error, 'release');
    toast.error(conversationLimitErrorMessage(error), toastConfig);
    return { ok: false, code: getConversationLimitErrorCode(error) };
  }
};

/** Admin "Carga por agente" panel (R6). */
export const fetchLoadAction = () => async (dispatch) => {
  dispatch(A.loadRequest());
  try {
    const load = await getAgentLoad();
    dispatch(A.loadSuccess(load));
  } catch (error) {
    reportUnexpected(error, 'load');
    dispatch(A.loadFailure(conversationLimitErrorMessage(error)));
  }
};

/**
 * The open panel re-reads /load in the background (queue shrank without
 * emptying: its oldest wait may have left). No spinner; a failure keeps what
 * is on screen and goes to Sentry only if unexpected.
 */
export const refreshLoadQuietlyAction = () => async (dispatch) => {
  dispatch(A.loadRequest({ quiet: true }));
  try {
    dispatch(A.loadSuccess(await getAgentLoad()));
  } catch (error) {
    reportUnexpected(error, 'load.quiet');
  }
};

export const fetchConfigAction = (options) => async (dispatch) => {
  dispatch(A.configRequest(options));
  try {
    dispatch(A.configSuccess(await getLimitConfig()));
  } catch (error) {
    reportUnexpected(error, 'config');
    dispatch(A.configFailure(conversationLimitErrorMessage(error)));
  }
};

/** Validates the form locally; the backend validates again (spec §5). */
export const saveConfigAction = (form) => async (dispatch) => {
  const { ok, payload, errors } = buildConfigPayload(form);
  if (!ok) return { ok: false, errors };
  dispatch(A.configSaveRequest());
  try {
    dispatch(A.configSuccess(await saveLimitConfig(payload)));
    toast.success('Configuración guardada.', toastConfig);
    return { ok: true, errors: {} };
  } catch (error) {
    reportUnexpected(error, 'saveConfig');
    const message = conversationLimitErrorMessage(error);
    dispatch(A.configFailure(message));
    toast.error(message, toastConfig);
    return { ok: false, errors: {} };
  }
};
